import { useEffect, useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import type { ButtonComponent, ComponentRow, EntitySelectComponent, SelectComponent } from '@spawnwire/shared';
import { Button, clsx } from '../../components/ui';
import { loadMembers } from '../../lib/data';
import { channelsOfSpace, useStore } from '../../store/store';
import { clickComponent } from './componentActions';

const BUTTON_CLASS: Record<Exclude<ButtonComponent['style'], 'link'>, string> = {
  primary: 'bg-accent text-accent-fg hover:bg-accent-hover',
  secondary: 'bg-active text-fg hover:bg-line-strong',
  success: 'bg-green-600 text-white hover:brightness-110',
  danger: 'bg-danger text-white hover:brightness-110',
};

const base = 'inline-flex h-8 max-w-full items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50';

function BotButton({ b, channelId, messageId }: { b: ButtonComponent; channelId: string; messageId: string }) {
  const [busy, setBusy] = useState(false);
  if (b.style === 'link') {
    return (
      <a href={b.url} target="_blank" rel="noopener noreferrer nofollow ugc" className={clsx(base, BUTTON_CLASS.secondary)}>
        <span className="truncate">{b.label}</span>
        <ExternalLink className="size-3.5 shrink-0" aria-hidden />
      </a>
    );
  }
  return (
    <button
      type="button"
      disabled={b.disabled || busy}
      className={clsx(base, BUTTON_CLASS[b.style])}
      onClick={() => {
        setBusy(true);
        void clickComponent(channelId, messageId, b.customId!).finally(() => setBusy(false));
      }}
    >
      <span className="truncate">{b.label}</span>
    </button>
  );
}

interface Choice {
  value: string;
  label: string;
  description?: string;
}

/**
 * A menu of choices. With one choice allowed, picking it sends it; with several, people tick them and confirm. Both the
 * menus a bot lists itself and the ones that pick people, roles or channels are drawn this way.
 */
function ChoiceMenu({
  choices,
  initial,
  placeholder,
  name,
  minValues,
  maxValues,
  disabled,
  onSend,
}: {
  choices: Choice[];
  initial: string[];
  placeholder?: string;
  name: string;
  minValues: number;
  maxValues: number;
  disabled: boolean;
  onSend: (values: string[]) => Promise<unknown>;
}) {
  const [picked, setPicked] = useState<string[]>(initial);
  const [busy, setBusy] = useState(false);
  const send = (values: string[]) => {
    setBusy(true);
    void onSend(values).finally(() => setBusy(false));
  };
  const field = 'h-9 w-full max-w-sm rounded-md border border-line bg-input px-2 text-sm text-fg outline-none focus:border-accent disabled:cursor-not-allowed disabled:opacity-50';

  // One choice: picking it sends it. The menu goes back to its starting point, as the bot may change the message.
  if (maxValues === 1) {
    return (
      <select
        aria-label={placeholder ?? name}
        className={field}
        disabled={disabled || busy}
        value={picked[0] ?? ''}
        onChange={(e) => {
          send([e.target.value]);
          setPicked(initial);
        }}
      >
        <option value="" disabled>
          {placeholder ?? 'Choose…'}
        </option>
        {choices.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  // Several choices: tick them, then confirm.
  const enough = picked.length >= minValues && picked.length <= maxValues;
  return (
    <fieldset className="w-full max-w-sm rounded-md border border-line bg-input p-2" disabled={disabled || busy}>
      <legend className="px-1 text-xs text-muted">{placeholder ?? 'Choose'}</legend>
      <div className="scroll-thin flex max-h-56 flex-col gap-1 overflow-y-auto">
        {choices.map((o) => {
          const on = picked.includes(o.value);
          return (
            <label key={o.value} className="flex cursor-pointer items-start gap-2 rounded px-1 py-0.5 text-sm hover:bg-hover">
              <input
                type="checkbox"
                className="mt-1"
                checked={on}
                disabled={!on && picked.length >= maxValues}
                onChange={() => setPicked((p) => (on ? p.filter((v) => v !== o.value) : [...p, o.value]))}
              />
              <span>
                {o.label}
                {o.description && <span className="block text-xs text-muted">{o.description}</span>}
              </span>
            </label>
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-xs text-muted">{minValues === maxValues ? `Choose ${maxValues}` : `Choose ${minValues} to ${maxValues}`}</span>
        <Button size="sm" disabled={!enough} onClick={() => send(picked)}>
          Confirm
        </Button>
      </div>
    </fieldset>
  );
}

function BotSelect({ s, channelId, messageId }: { s: SelectComponent; channelId: string; messageId: string }) {
  return (
    <ChoiceMenu
      choices={s.options}
      initial={s.options.filter((o) => o.default).map((o) => o.value)}
      placeholder={s.placeholder}
      name={s.customId}
      minValues={s.minValues}
      maxValues={s.maxValues}
      disabled={s.disabled}
      onSend={(values) => clickComponent(channelId, messageId, s.customId, values)}
    />
  );
}

/**
 * A menu of this space's people, roles or channels. What is offered is only what the person already sees here (channels
 * they can see, the space's members, its roles); the server checks the choice again when it is sent.
 */
function BotEntitySelect({ s, channelId, messageId }: { s: EntitySelectComponent; channelId: string; messageId: string }) {
  const spaceId = useStore((st) => st.channels[channelId]?.spaceId ?? null);
  const members = useStore((st) => (spaceId ? st.members[spaceId] : undefined));
  const spaceRoles = useStore((st) => (spaceId ? st.spaces[spaceId]?.roles : undefined));
  const allChannels = useStore((st) => st.channels);
  useEffect(() => {
    if (s.type === 'user_select' && spaceId) void loadMembers(spaceId).catch(() => {});
  }, [s.type, spaceId]);

  const choices = useMemo<Choice[]>(() => {
    if (!spaceId) return [];
    if (s.type === 'user_select') {
      return Object.values(members ?? {})
        .map((m) => ({ value: m.user.id, label: m.nickname ?? m.user.displayName, description: `@${m.user.username}` }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    if (s.type === 'role_select') {
      return (spaceRoles ?? []).filter((r) => !r.isDefault).sort((a, b) => b.position - a.position).map((r) => ({ value: r.id, label: r.name }));
    }
    return channelsOfSpace({ channels: allChannels }, spaceId)
      .filter((c) => c.type !== 'thread')
      .map((c) => ({ value: c.id, label: c.type === 'category' ? c.name.toUpperCase() : c.type === 'voice' || c.type === 'stage' ? c.name : `#${c.name}` }));
  }, [s.type, spaceId, members, spaceRoles, allChannels]);

  return (
    <ChoiceMenu
      choices={choices}
      initial={[]}
      placeholder={s.placeholder ?? (s.type === 'user_select' ? 'Choose a person…' : s.type === 'role_select' ? 'Choose a role…' : 'Choose a channel…')}
      name={s.customId}
      minValues={s.minValues}
      maxValues={s.maxValues}
      disabled={s.disabled || choices.length === 0}
      onSend={(values) => clickComponent(channelId, messageId, s.customId, values)}
    />
  );
}

/** The buttons and menus a bot attached to a message. What they do is decided by the bot; the server checks every click. */
export function MessageComponents({ rows, channelId, messageId }: { rows: ComponentRow[]; channelId: string; messageId: string }) {
  return (
    <div className="mt-1.5 flex flex-col gap-1.5" data-testid="message-components">
      {rows.map((row, i) => (
        <div key={i} className="flex flex-wrap items-center gap-1.5">
          {row.components.map((c, n) =>
            c.type === 'button' ? (
              <BotButton key={c.customId ?? `${i}-${n}`} b={c} channelId={channelId} messageId={messageId} />
            ) : c.type === 'select' ? (
              <BotSelect key={c.customId} s={c} channelId={channelId} messageId={messageId} />
            ) : (
              <BotEntitySelect key={c.customId} s={c} channelId={channelId} messageId={messageId} />
            ),
          )}
        </div>
      ))}
    </div>
  );
}
